import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { addSuccessNotification, NotificationType, updateMemberEmail } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { forwardRef, useMemo, useState } from 'react';
import { useFormValidator } from '../../hooks/use-form-validator';
import { Form } from '../../subcomponents/form/form/form';
import { getIdForComponentNotification } from '../../utils/get-id-for-component-notification';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { UpdateMemberEmailFormContextInstance } from './types/context';
import { defaultUpdateMemberEmailFormData } from './types/data';
import { defaultUpdateMemberEmailFormProps, updateMemberEmailFormPropsValidator } from './types/props';
import { handleUpdateEmailApiError } from './utils/handle-update-email-api-error';
const b = bemClassBuilder('v-update-member-email-form');
const UpdateMemberEmailFormBase = (props, ref) => {
    const { config, displayText, events, children } = props;
    const [formData, setFormData] = useState(defaultUpdateMemberEmailFormData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const validationConfig = config.validation;
    const { validationResult, validator, setValidationResult } = useFormValidator(validationConfig);
    const handleSubmit = async () => {
        const { captcha } = formData, request = __rest(formData, ["captcha"]);
        await updateMemberEmail(request, captcha);
        addSuccessNotification({
            id: getIdForComponentNotification('update-email', NotificationType.Success, 'submit'),
            messages: [displayText.updateEmailSuccessfulMessage]
        });
    };
    const handleFailedUpdate = (error) => {
        var _a;
        handleUpdateEmailApiError(error, displayText, setValidationResult);
        (_a = events.onAfterFailedSubmit) === null || _a === void 0 ? void 0 : _a.call(events);
    };
    const handleFormSubmittingStateChanged = (submitting) => {
        var _a;
        setIsSubmitting(submitting);
        (_a = events.onFormSubmittingStateChanged) === null || _a === void 0 ? void 0 : _a.call(events, submitting);
    };
    const context = useMemo(() => ({
        formData,
        validationResult,
        setFormData,
        onChange: (fieldName, value) => setFormData(data => (Object.assign(Object.assign({}, data), { [fieldName]: value }))),
        validationConfig,
        validator,
        disabled: isSubmitting,
        loading: false
    }), [formData, validationResult, isSubmitting, validationConfig, validator]);
    return (_jsx(Form, Object.assign({}, props, { id: props.formId, className: b.block(), validator: () => validator.validateForm(formData), events: Object.assign(Object.assign({}, props.events), { onSubmit: handleSubmit, onAfterFailedSubmit: handleFailedUpdate, onFormSubmittingStateChanged: handleFormSubmittingStateChanged }), setFormData: setFormData, ref: ref }, { children: _jsx(UpdateMemberEmailFormContextInstance.Provider, Object.assign({ value: context }, { children: children })) })));
};
const UpdateMemberEmailForm = observer(forwardRef(UpdateMemberEmailFormBase));
UpdateMemberEmailForm.displayName = 'UpdateMemberEmailForm';
/**
 * The **Update Member Email Form** component renders a form that allows a member to update their email.
 */
export const UpdateMemberEmailFormComponent = withComponentWrapper(UpdateMemberEmailForm, defaultUpdateMemberEmailFormProps, updateMemberEmailFormPropsValidator);
